import { useContext, useEffect, useState } from "react";
import {
  useFloating,
  offset,
  flip,
  shift,
  size as floatingSize,
  autoUpdate,
  FloatingPortal,
  useClick,
  useDismiss,
  useInteractions,
} from "@floating-ui/react";
import { buildClasses } from "../utils/buildClasses";
import { RyeButton } from "../rye/RyeButton";
import { RyeDropDown } from "../rye/RyeDropDown";
import { RyeDropDownMultiSelect } from "../rye/RyeDropDownMultiSelect";
import { RyeIcon } from "../rye/RyeIcon";
import { TransactionReviewedIcon } from "./TransactionReviewedIcon";
import { CategoriesContext } from "../firebaseio/CategoriesContext";
import { CategoryGroupsContext } from "../firebaseio/CategoryGroupsContext";
import { TagsContext } from "../firebaseio/TagsContext";
import { UserContext } from "../firebaseio/UserContext";
import { firestoreUpdateTransaction } from "../firebaseio/firestoreIo";
import { getFirestore, writeBatch } from "firebase/firestore";
import { getApp } from "firebase/app";
import { FirestoreDocTag } from "breadcommon";
import { TagDot } from "../tags_screen/TagDot";
import { getTagColor } from "../tags_screen/tagColors";

type ReviewedStatus = "reviewed" | "unreviewed";
type FieldType = "category" | "tags" | "reviewed";

const NO_CATEGORY_ID = "__no_category__";
const NO_TAGS_ID = "__no_tags__";

export function BulkEditBar({
  selectedTransactionIds,
  deselectAll,
}: {
  selectedTransactionIds: Set<string>;
  deselectAll: () => void;
}): JSX.Element | null {
  const categories = useContext(CategoriesContext);
  const categoryGroups = useContext(CategoryGroupsContext);
  const user = useContext(UserContext);

  const [selectedField, setSelectedField] = useState<FieldType>("category");
  const [selectedCategoryId, setSelectedCategoryId] = useState<string | null>(
    null,
  );
  const [selectedTagIds, setSelectedTagIds] = useState<string[] | null>(null);
  const [selectedReviewedStatus, setSelectedReviewedStatus] =
    useState<ReviewedStatus | null>(null);

  const count = selectedTransactionIds.size;
  const isVisible = count > 1;

  useEffect(() => {
    if (!isVisible) {
      setSelectedField("category");
      setSelectedCategoryId(null);
      setSelectedTagIds(null);
      setSelectedReviewedStatus(null);
    }
  }, [isVisible]);

  if (!isVisible) {
    return null;
  }

  const hasValue =
    (selectedField === "category" && selectedCategoryId !== null) ||
    (selectedField === "tags" && selectedTagIds !== null) ||
    (selectedField === "reviewed" && selectedReviewedStatus !== null);

  const buttonText = `Update ${count} transaction${count !== 1 ? "s" : ""}`;

  function resetState() {
    setSelectedField("category");
    setSelectedCategoryId(null);
    setSelectedTagIds(null);
    setSelectedReviewedStatus(null);
  }

  async function handleApply() {
    if (!hasValue) return;

    const db = getFirestore(getApp());
    const batch = writeBatch(db);

    for (const transactionId of Array.from(selectedTransactionIds)) {
      const update: any = { manual_data: {} };
      if (selectedField === "category") {
        update.manual_data.category_id =
          selectedCategoryId === NO_CATEGORY_ID ? null : selectedCategoryId;
      } else if (selectedField === "tags") {
        update.manual_data.tag_ids = selectedTagIds?.includes(NO_TAGS_ID)
          ? []
          : selectedTagIds;
      } else if (selectedField === "reviewed") {
        update.manual_data.reviewed = selectedReviewedStatus === "reviewed";
      }
      firestoreUpdateTransaction(user.uid, transactionId, update, batch);
    }

    await batch.commit();

    resetState();
    deselectAll();
  }

  function handleDeselectAll() {
    resetState();
    deselectAll();
  }

  const fieldEntries = [
    { id: "category" as FieldType, text: "Category", icon: null },
    { id: "tags" as FieldType, text: "Tags", icon: null },
    { id: "reviewed" as FieldType, text: "Review status", icon: null },
  ];

  const categoryEntries = [
    {
      id: NO_CATEGORY_ID,
      text: "No category",
      icon: null,
    },
    ...Array.from(categories.values())
      .sort((a, b) => a.order_position - b.order_position)
      .map((category) => ({
        id: category.id,
        text: category.name,
        icon: category.emoji,
        groupId: category.category_group_id,
      })),
  ];

  const categoryGroupEntries = Array.from(categoryGroups.values())
    .sort((a, b) => a.order_position - b.order_position)
    .map((group) => ({
      id: group.id,
      label: group.name,
    }));

  const reviewedEntries = [
    {
      id: "reviewed" as ReviewedStatus,
      text: "Reviewed",
      icon: <TransactionReviewedIcon reviewed={true} />,
    },
    {
      id: "unreviewed" as ReviewedStatus,
      text: "Unreviewed",
      icon: <TransactionReviewedIcon reviewed={false} />,
    },
  ];

  return (
    <div
      className={buildClasses(
        "absolute",
        "bottom-6",
        "left-1/2",
        "z-50",
        "flex",
        "items-center",
        "gap-3",
        "px-4",
        "py-2.5",
        "rounded-full",
        "bg-surface-50",
        "border",
        "border-on-surface-200",
        "shadow-xl",
        "animate-slide-up-fade-in",
      )}
      onMouseDown={(e) => e.stopPropagation()}
    >
      <RyeButton
        icon="close"
        size="sm"
        shape="fully-round"
        variant="transparent"
        vibe="subdued"
        onClick={handleDeselectAll}
        tooltip="Deselect all"
      />

      <div
        className={buildClasses(
          "w-px",
          "h-6",
          "bg-on-surface-200",
          "flex-shrink-0",
        )}
      />

      {/* Field type dropdown */}
      <div className={buildClasses("w-40")}>
        <RyeDropDown<FieldType>
          initEntryId={selectedField}
          entries={fieldEntries}
          onSelectEntry={(entry) => {
            setSelectedField(entry.id);
            setSelectedCategoryId(null);
            setSelectedTagIds(null);
            setSelectedReviewedStatus(null);
          }}
          placeholder="Field..."
          size="sm"
          width="full"
          variant="outlined"
          tooltip="Select field to bulk edit"
        />
      </div>

      {/* Value selector based on field */}
      {selectedField === "category" && (
        <div className={buildClasses("w-48")}>
          <RyeDropDown<string>
            initEntryId={selectedCategoryId}
            entries={categoryEntries}
            groups={categoryGroupEntries}
            onSelectEntry={(entry) => setSelectedCategoryId(entry.id)}
            searchable
            placeholder="Select category"
            size="sm"
            width="full"
            variant="outlined"
            parentColor={selectedCategoryId !== null ? "purple" : "normal"}
            tooltip="Select new category"
          />
        </div>
      )}

      {selectedField === "tags" && (
        <BulkEditTagsSelector
          selectedTagIds={selectedTagIds}
          onChange={setSelectedTagIds}
        />
      )}

      {selectedField === "reviewed" && (
        <div className={buildClasses("w-40")}>
          <RyeDropDown<ReviewedStatus>
            initEntryId={selectedReviewedStatus}
            entries={reviewedEntries}
            onSelectEntry={(entry) => setSelectedReviewedStatus(entry.id)}
            placeholder="Select"
            size="sm"
            width="full"
            variant="outlined"
            parentColor={selectedReviewedStatus !== null ? "purple" : "normal"}
            tooltip="Select new status"
          />
        </div>
      )}

      <RyeButton
        text={buttonText}
        size="sm"
        shape="fully-round"
        variant="filled"
        vibe="primary"
        onClick={handleApply}
        disabled={!hasValue}
      />
    </div>
  );
}

function BulkEditTagsSelector({
  selectedTagIds,
  onChange,
}: {
  selectedTagIds: string[] | null;
  onChange: (ids: string[] | null) => void;
}): JSX.Element {
  const tags = useContext(TagsContext);
  const [isOpen, setIsOpen] = useState<boolean>(false);

  const { refs, floatingStyles, context, placement } = useFloating({
    open: isOpen,
    onOpenChange: setIsOpen,
    placement: "bottom-start",
    whileElementsMounted: autoUpdate,
    middleware: [
      offset(2),
      flip(),
      shift(),
      floatingSize({
        apply({ rects, elements }) {
          Object.assign(elements.floating.style, {
            width: `${rects.reference.width}px`,
          });
        },
      }),
    ],
  });
  const click = useClick(context);
  const dismiss = useDismiss(context);
  const { getReferenceProps, getFloatingProps } = useInteractions([
    click,
    dismiss,
  ]);

  const sortedTags: FirestoreDocTag[] = Array.from(tags.values()).sort(
    (a, b) => a.order_position - b.order_position,
  );
  const tagEntries = [
    { id: NO_TAGS_ID, text: "No tags", icon: null },
    ...sortedTags.map((tag) => ({
      id: tag.id,
      text: tag.name,
      icon: <TagDot colorId={tag.color} size="sm" />,
    })),
  ];

  const isNoTagsSelected = selectedTagIds?.includes(NO_TAGS_ID) ?? false;
  const selectedActualTags = sortedTags.filter((tag) =>
    selectedTagIds?.includes(tag.id),
  );

  function handleSelectionChange(newIds: string[]): void {
    const wasNoTags = selectedTagIds?.includes(NO_TAGS_ID) ?? false;
    const hasNoTags = newIds.includes(NO_TAGS_ID);
    if (hasNoTags && !wasNoTags) {
      onChange([NO_TAGS_ID]);
    } else if (hasNoTags && newIds.length > 1) {
      onChange(newIds.filter((id) => id !== NO_TAGS_ID));
    } else {
      onChange(newIds);
    }
  }

  return (
    <>
      <button
        ref={refs.setReference}
        type="button"
        {...getReferenceProps()}
        className={buildClasses(
          "w-48",
          "h-7",
          "px-2",
          "flex",
          "items-center",
          "gap-1",
          "rounded-md",
          "border",
          "border-on-surface-300",
          "bg-surface-50",
          "text-sm",
          "text-left",
          "cursor-pointer",
          "overflow-hidden",
        )}
      >
        <div
          className={buildClasses(
            "flex-1",
            "min-w-0",
            "flex",
            "items-center",
            "gap-1",
            "overflow-x-auto",
          )}
        >
          {selectedTagIds === null ? (
            <span className={buildClasses("text-on-surface-400", "truncate")}>
              Select tags
            </span>
          ) : isNoTagsSelected ? (
            <span
              className={buildClasses(
                "flex-shrink-0",
                "px-1.5",
                "py-0.5",
                "rounded",
                "text-xs",
                "bg-on-surface-100",
                "text-on-surface-600",
              )}
            >
              No tags
            </span>
          ) : selectedActualTags.length === 0 ? (
            <span className={buildClasses("text-on-surface-400", "truncate")}>
              Select tags
            </span>
          ) : (
            selectedActualTags.map((tag) => {
              const color = getTagColor(tag.color);
              return (
                <span
                  key={tag.id}
                  title={tag.name}
                  className={buildClasses(
                    "flex-shrink-0",
                    "px-1.5",
                    "py-0.5",
                    "rounded",
                    "text-xs",
                  )}
                  style={{ backgroundColor: color.bg, color: color.text }}
                >
                  {tag.name}
                </span>
              );
            })
          )}
        </div>
        <RyeIcon
          name={isOpen ? "keyboard_arrow_up" : "keyboard_arrow_down"}
          size="sm"
        />
      </button>
      {isOpen && (
        <FloatingPortal>
          <div
            ref={refs.setFloating}
            style={floatingStyles}
            {...getFloatingProps()}
            className={buildClasses("z-[2000000000]")}
          >
            <RyeDropDownMultiSelect<string>
              selectedEntryIds={selectedTagIds ?? []}
              entries={tagEntries}
              onSelectionChange={handleSelectionChange}
              alwaysExpanded
              onEsc={() => setIsOpen(false)}
              flipped={placement.startsWith("top")}
              searchable
              showSelectedItemsRow={false}
              placeholder="Search tags"
              size="sm"
              width="full"
              variant="outlined"
            />
          </div>
        </FloatingPortal>
      )}
    </>
  );
}
